import { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { attendanceConfig, attendanceMonth, createAttProposal, attInbox, decideAttProposal, getHolidays, setHolidays, importAttendance, getEmpMap, setEmpMap, purgeAttendanceMonth, attLockStatus, lockAttMonth, unlockAttMonth, getLateExempt, setLateExempt } from '../api/attendance';
import { parseAttendanceXlsx } from '../utils/parseAttendanceXlsx';
import AttendanceMatrix from './AttendanceMatrix';
import FaceAttendanceModal from '../components/FaceAttendanceModal';
import OnlineAttAdminModal from '../components/OnlineAttAdminModal';

const WD = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const colOf = (wd) => (wd === 0 ? 6 : wd - 1); // weekday 0=CN..6=T7 -> cột 0..6 (T2..CN)
// Màu theo trạng thái hiệu lực.
const ST = {
  work: { bg: 'color-mix(in srgb, var(--ok) 13%, transparent)', bd: 'color-mix(in srgb, var(--ok) 40%, transparent)', tx: 'var(--ok)' },
  warn: { bg: 'color-mix(in srgb, var(--warn-fill) 16%, transparent)', bd: 'color-mix(in srgb, var(--warn-fill) 45%, transparent)', tx: 'var(--warn)' },
  absent: { bg: 'color-mix(in srgb, var(--danger) 11%, transparent)', bd: 'color-mix(in srgb, var(--danger) 38%, transparent)', tx: 'var(--danger)' },
  half: { bg: 'color-mix(in srgb, var(--ok) 13%, transparent)', bd: 'color-mix(in srgb, var(--ok) 40%, transparent)', tx: 'var(--ok)' },
  off: { bg: 'var(--panel2)', bd: 'var(--border)', tx: 'var(--muted2)' },
};
// Ô nửa công (1/2 công): tô xanh 1 nửa, gạch chéo ở giữa.
const HALF_BG = 'linear-gradient(135deg, color-mix(in srgb, var(--ok) 26%, transparent) 0 calc(50% - 1px), var(--ok) calc(50% - 1px) calc(50% + 1px), transparent calc(50% + 1px) 100%)';
const labelOf = (d) => {
  if (d.status === 'off') return d.weekday === 0 ? 'CN' : 'Nghỉ (ĐK)';
  if (d.status === 'absent') return 'Nghỉ';
  if (d.status === 'half') return `${d.checkIn}–${d.checkOut} · ½`;
  if (d.checkCount >= 2) return `${d.checkIn}–${d.checkOut}`;
  if (d.checkCount === 1) return `${d.checkIn || d.checkOut} · 1 lần`;
  return '';
};

// Nhãn 3 LỖI PHẠT riêng biệt (khớp backend VIOL_KEYS): đi muộn / về sớm / quên chấm (vào hoặc ra).
const VIOL_LABELS = [['late', '⏰ Đi muộn'], ['early', '🏃 Về sớm'], ['miss', '📵 Quên chấm công']];

function ProposalModal({ types, date, viol, checkCount, day, onClose, onSubmit }) {
  const [type, setType] = useState(types[0]?.key || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Lỗi mà đơn giải trình (CHỈ loại 'việc công ty' — các loại nghỉ/công tác tự bỏ hết,
  // 'quên chấm công' theo quy định vẫn giữ phạt). Duyệt xong chỉ bỏ đúng lỗi đã tick.
  const [waives, setWaives] = useState(() => new Set());
  const dayViols = VIOL_LABELS.filter(([k]) => viol && viol[k]);
  const d = new Date(`${date}T00:00:00`);
  const toggleWaive = (k) => setWaives((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>📝 Đề xuất / giải trình — {String(d.getDate()).padStart(2, '0')}/{String(d.getMonth() + 1).padStart(2, '0')}/{d.getFullYear()}</h3>
      {/* TÓM TẮT NGÀY — bấm vào ô là thấy RÕ giờ chấm + công + (các) lỗi trong ngày. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px', marginBottom: 10, background: 'var(--panel2)' }}>
        <div className="small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>🕒 Giờ chấm: <b>{day?.checkIn || day?.checkOut ? `${day.checkIn || '—'}${day.checkOut ? `–${day.checkOut}` : ''}` : 'chưa chấm'}</b>{checkCount === 1 ? ' (chỉ 1 lần)' : ''}</span>
          <span>📊 Công: <b>{day && day.cong != null ? day.cong : '—'}</b></span>
        </div>
        {dayViols.length > 0 ? (
          <div style={{ marginTop: 6 }}>
            <div className="small" style={{ color: 'var(--danger)', fontWeight: 700 }}>Lỗi trong ngày (mỗi lỗi phạt 50.000đ):</div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
              {dayViols.map(([k, l]) => <li key={k} style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{l}</li>)}
            </ul>
          </div>
        ) : (
          <div className="small" style={{ marginTop: 6, color: 'var(--ok)', fontWeight: 600 }}>✓ Ngày này không có lỗi phạt</div>
        )}
      </div>
      {checkCount === 1 && (
        <div className="small" style={{ marginBottom: 8, color: 'var(--warn)' }}>
          Ngày này chỉ chấm 1 lần (nửa công). Giải trình “Quên chấm công” được duyệt sẽ tính ĐỦ CÔNG
          kèm 1 lỗi phạt 50.000đ.
        </div>
      )}
      <div className="field">
        <label>Hình thức giải trình</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      {(type === 'viec_cong_ty' || type === 'khac') && dayViols.length > 0 && (
        <div className="field">
          <label>Đơn này giải trình cho lỗi (duyệt xong CHỈ bỏ lỗi đã chọn)</label>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            {dayViols.map(([k, l]) => (
              <label key={k} className="row" style={{ gap: 5, alignItems: 'center', cursor: 'pointer', fontWeight: 400 }}>
                <input type="checkbox" checked={waives.has(k)} onChange={() => toggleWaive(k)} style={{ width: 'auto' }} />
                {l}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="field">
        <label>Lý do giải trình{type === 'khac' && <span style={{ color: 'var(--danger)' }}> (bắt buộc)</span>}</label>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder={type === 'khac' ? 'Nêu rõ lý do của bạn…' : 'Nhập lý do…'} />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={busy || (type === 'khac' && !reason.trim())}
          title={type === 'khac' && !reason.trim() ? 'Vui lòng nhập lý do' : undefined}
          onClick={async () => { setBusy(true); try { await onSubmit(type, reason, [...waives]); } finally { setBusy(false); } }}>
          {busy ? 'Đang gửi…' : 'Đề xuất'}
        </button>
      </div>
    </div>
  );
}

// Modal CHI TIẾT NGÀY — CHỈ ĐỌC, cho NHÂN VIÊN tự xem: đề xuất đã duyệt chưa + kế toán đã chốt công chưa.
// Không có nút chỉnh (Đi muộn/Về sớm/chốt công) — đó là quyền kế toán. Nếu được phép -> có nút mở form đề xuất.
function DayDetailModal({ date, day, proposal, types, canPropose, onClose, onPropose }) {
  const d = new Date(`${date}T00:00:00`);
  const dd = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  const dayViols = VIOL_LABELS.filter(([k]) => day?.viol?.[k]);
  const typeLabel = proposal ? ((types || []).find((t) => t.key === proposal.type)?.label || proposal.typeLabel || 'Đơn đề xuất') : '';
  const st = proposal?.status;
  const badge = st === 'approved' ? ['Đã duyệt', 'var(--ok)'] : st === 'rejected' ? ['Từ chối', 'var(--danger)'] : ['Chờ duyệt', 'var(--warn-fill)'];
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>🔎 Chi tiết ngày {dd}</h3>
      {/* ĐỀ XUẤT + TRẠNG THÁI DUYỆT */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: 'var(--panel2)' }}>
        {proposal ? (
          <>
            <div style={{ fontWeight: 700 }}>
              {typeLabel}
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999, color: '#fff', background: badge[1] }}>{badge[0]}</span>
            </div>
            {proposal.reason && <div className="small muted" style={{ marginTop: 5 }}>Lý do: {proposal.reason}</div>}
            {st === 'rejected' && proposal.decideNote && <div className="small" style={{ marginTop: 4, color: 'var(--danger)' }}>Ghi chú từ chối: {proposal.decideNote}</div>}
          </>
        ) : <div className="small muted">Ngày này chưa có đơn đề xuất / giải trình.</div>}
      </div>
      {/* CHẤM CÔNG + SỐ CÔNG */}
      <div className="small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <span>🕒 Chấm: <b>{day && (day.checkIn || day.checkOut) ? `${day.checkIn || '—'}${day.checkOut ? `–${day.checkOut}` : ''}` : 'không có'}</b></span>
        <span>📊 Công: <b>{day && day.cong != null ? day.cong : '—'}</b></span>
      </div>
      {/* LỖI NGÀY */}
      {day && (dayViols.length > 0
        ? <div className="small" style={{ marginBottom: 10 }}><span style={{ color: 'var(--danger)', fontWeight: 700 }}>Lỗi trong ngày (50.000đ/lỗi): </span>{dayViols.map(([, l]) => l).join(' · ')}</div>
        : <div className="small" style={{ marginBottom: 10, color: 'var(--ok)', fontWeight: 600 }}>✓ Không có lỗi phạt</div>)}
      {/* KẾ TOÁN ĐÃ CHỐT LẠI CHƯA */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        {day?.adminAdjusted
          ? <div className="small" style={{ color: 'var(--ok)', fontWeight: 700 }}>✓ Kế toán đã chốt lại ngày này — tính {day.cong ?? 0} công.</div>
          : <div className="small muted">Kế toán chưa chốt lại — đang tính tự động{day && day.cong != null ? ` (${day.cong} công)` : ''}.</div>}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        {canPropose && <button className="btn" onClick={onPropose}>📝 Đề xuất / giải trình</button>}
        <button className="btn ghost" onClick={onClose}>Đóng</button>
      </div>
    </div>
  );
}

// Modal chọn 1 THÁNG để up cho cả đợt (đơn giản nhất, khỏi lệ thuộc đoán tháng từ file).
function ImportConfirm({ files, defaultMonth, onClose, onConfirm }) {
  const [month, setMonth] = useState(defaultMonth || files.find((f) => f.month)?.month || '');
  const valid = /^\d{4}-\d{2}$/.test(month);
  const label = (m) => (/^\d{4}-\d{2}$/.test(m) ? `tháng ${m.slice(5, 7)}/${m.slice(0, 4)}` : '—');
  const diff = files.some((f) => f.month && f.month !== month);
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>⭱ Cập nhật bảng chấm công</h3>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>Up cho tháng</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="small muted" style={{ marginBottom: 10 }}>Toàn bộ dữ liệu trong {files.length} file dưới đây sẽ ghi vào <b style={{ color: 'var(--accent)' }}>{valid ? label(month) : '…'}</b>.</div>
      <div className="tbl-wrap framed">
        <table style={{ fontSize: 13 }}>
          <thead><tr><th>File</th><th className="c">Số NV</th><th>Tháng trong file</th></tr></thead>
          <tbody>
            {files.map((f, i) => (
              <tr key={i}>
                <td className="small">{f.name}</td>
                <td className="c">{f.count}</td>
                <td className="small muted">{f.month ? label(f.month) : '(không rõ)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {diff && <div className="small" style={{ color: 'var(--warn)', marginTop: 8 }}>⚠ Có file ghi tháng khác trong dữ liệu — vẫn sẽ ghi vào {label(month)} theo lựa chọn của bạn.</div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={!valid} onClick={() => onConfirm(month)}>Cập nhật {label(month)}</button>
      </div>
    </div>
  );
}

// MIỄN ĐI MUỘN: admin TÍCH danh sách nhân viên được phép đi muộn mà KHÔNG bị tính phạt.
// Danh sách tích là NGUỒN SỰ THẬT — bỏ tích ai thì người đó bị tính phạt muộn trở lại.
function LateExemptModal({ onClose, toast, onSaved }) {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getLateExempt()
      .then((list) => { setRows(list); setPicked(new Set(list.filter((u) => u.noLatePenalty).map((u) => String(u.id)))); })
      .catch(() => { setRows([]); toast('Không tải được danh sách', 'danger'); });
  }, []); // eslint-disable-line
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const ql = q.trim().toLowerCase();
  const filtered = (rows || []).filter((u) => !ql || [u.name, u.dept].some((v) => String(v || '').toLowerCase().includes(ql)));
  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await setLateExempt([...picked]);
      toast(`Đã cập nhật danh sách miễn đi muộn (${picked.size} người)`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast(e.response?.data?.message || 'Lưu thất bại', 'danger'); }
    finally { setBusy(false); }
  }
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>⏰ Miễn tính đi muộn</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Tích những nhân viên được phép <b>đi muộn không bị tính phạt</b>. Chỉ miễn lỗi đi muộn — về sớm / quên chấm vẫn tính như thường. Bỏ tích để tính phạt trở lại.
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm tên / phòng ban…" style={{ width: '100%', marginBottom: 8 }} />
      {rows == null ? (
        <div className="empty">Đang tải…</div>
      ) : (
        <div className="tbl-wrap framed" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 && <div className="small muted" style={{ padding: 10 }}>Không có nhân viên phù hợp.</div>}
          {filtered.map((u) => (
            <label key={u.id} className="row" style={{ gap: 10, alignItems: 'center', padding: '7px 12px', cursor: 'pointer', fontWeight: 400, borderTop: '1px solid var(--line)' }}>
              <input type="checkbox" checked={picked.has(String(u.id))} onChange={() => toggle(u.id)} style={{ width: 'auto' }} />
              <span>{u.name}</span>
              {u.role === 'TP' && <span className="small muted">· TP</span>}
              <span className="small muted" style={{ marginLeft: 'auto' }}>{u.dept || ''}</span>
            </label>
          ))}
        </div>
      )}
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <span className="small muted">Đã chọn: <b style={{ color: 'var(--accent)' }}>{picked.size}</b></span>
        <span className="row" style={{ gap: 8 }}>
          <button className="btn ghost" onClick={onClose}>Hủy</button>
          <button className="btn" disabled={busy || rows == null} onClick={save}>{busy ? 'Đang lưu…' : 'Lưu'}</button>
        </span>
      </div>
    </div>
  );
}

export default function AttendanceTab({ me }) {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [cfg, setCfg] = useState({ types: [], employees: [], me: {} });
  const [userId, setUserId] = useState('');
  const calRef = useRef(null); // khung LỊCH cá nhân — cuộn tới khi bấm tên trong bảng toàn phòng
  // Mặc định: LUÔN tháng hiện tại.
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ days: [], stats: {} });
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holidays, setHol] = useState(0);
  const [importing, setImporting] = useState(false);
  const [empmap, setEmpmap] = useState({ mappings: [], roster: [] });
  const [showMap, setShowMap] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingCode, setSavingCode] = useState(''); // mã đang lưu ánh xạ (hiện spinner)
  const [savedCode, setSavedCode] = useState(''); // mã vừa lưu xong (hiện ✓ Đã lưu)
  const [unlockedCodes, setUnlockedCodes] = useState(() => new Set()); // mã đã gán được mở khoá tạm để sửa
  const [lock, setLock] = useState({ locked: false, lockedBy: '', canUnlock: false });

  const canManage = cfg.me.canManage;
  // Duyệt đơn: role TP, GĐ/CV, HOẶC trưởng phòng ban (kể cả role NV như TP kế toán/DVKH).
  const canApprove = me.role === 'TP' || me.role === 'GD' || me.role === 'CV' || (me.deptHeadOf || []).length > 0;
  const viewingSelf = !userId || String(userId) === String(me.id || me._id);
  const canPropose = (me.role === 'TP' || me.role === 'NV') && viewingSelf && !lock.locked;

  useEffect(() => { attendanceConfig().then((c) => { setCfg(c); if (c.employees.length && !c.me.canAttendance) setUserId(c.employees[0]._id); }).catch(() => {}); }, []);
  const loadMonth = () => { setLoading(true); attendanceMonth(userId, month).then(setData).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(loadMonth, [userId, month]); // eslint-disable-line
  useEffect(() => { getHolidays(month).then((d) => setHol(d.holidays)).catch(() => {}); }, [month]);
  // loadMap TRẢ VỀ promise (chỗ `await loadMap()` cần) -> KHÔNG được đưa thẳng vào useEffect:
  // React coi giá trị effect trả về là HÀM CLEANUP, unmount sẽ gọi promise() -> crash
  // "TypeError: ... is not a function" (màn hình gặp lỗi khi quản lý rời tab chấm công).
  const loadMap = () => { if (canManage) return getEmpMap().then(setEmpmap).catch(() => {}); };
  useEffect(() => { loadMap(); }, [canManage]); // eslint-disable-line
  const loadLock = () => { attLockStatus(month).then(setLock).catch(() => {}); };
  useEffect(loadLock, [month]); // eslint-disable-line
  const locked = lock.locked;
  async function toggleLock() {
    if (!locked) {
      if (!window.confirm(`CHỐT bảng chấm công tháng ${month}?\n\nSau khi chốt: KHÔNG import lại, KHÔNG dọn, KHÔNG sửa/xác nhận ngày, KHÔNG tạo đơn cho tháng này. Chỉ Giám đốc mới mở khoá lại được.`)) return;
      try { await lockAttMonth(month); toast(`Đã CHỐT chấm công tháng ${month}`); loadLock(); loadMonth(); setRefreshKey((k) => k + 1); }
      catch (e) { toast(e.response?.data?.message || 'Lỗi chốt', 'danger'); }
    } else {
      if (!window.confirm(`Mở khoá tháng ${month} để sửa lại?`)) return;
      try { await unlockAttMonth(month); toast(`Đã mở khoá tháng ${month}`); loadLock(); }
      catch (e) { toast(e.response?.data?.message || 'Lỗi mở khoá', 'danger'); }
    }
  }
  async function assignMap(excelCode, userId, force) {
    setSavingCode(excelCode);
    setSavedCode('');
    try {
      const r = await setEmpMap(excelCode, userId, force);
      await loadMap();
      loadMonth();
      setRefreshKey((k) => k + 1);
      setUnlockedCodes((s) => { const n = new Set(s); n.delete(excelCode); return n; }); // gán xong -> khoá lại
      toast(userId
        ? `Đã gán mã ${excelCode}${r?.applied ? ` · áp ${r.applied} ngày công` : ''}`
        : `Đã gỡ gán mã ${excelCode}${r?.purged ? ` · xoá ${r.purged} ngày` : ''}`);
      setSavedCode(excelCode);
      setTimeout(() => setSavedCode((c) => (c === excelCode ? '' : c)), 2500);
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi lưu ánh xạ', 'danger');
    } finally {
      setSavingCode('');
    }
  }

  // DỌN dữ liệu tháng trước khi up lại file — xử lý triệt để trùng mã (xoá ngày chưa chốt của cả tháng,
  // giữ ánh xạ + ngày đã duyệt). Phải import lại ĐẦY ĐỦ các file của tháng sau khi dọn.
  async function purgeMonth() {
    const ok = window.confirm(`Dọn toàn bộ chấm công CHƯA chốt của tháng ${month}?\n\nDùng khi bị TRÙNG MÃ / dữ liệu sai. Ngày đã duyệt đơn / kế toán đã xác nhận vẫn được giữ.\nSau khi dọn phải IMPORT LẠI ĐẦY ĐỦ các file của tháng.`);
    if (!ok) return;
    setImporting(true);
    try {
      const r = await purgeAttendanceMonth(month);
      toast(`Đã dọn ${r.deleted} ngày của tháng ${month}${r.kept ? ` · giữ ${r.kept} ngày đã chốt` : ''} — hãy import lại file`);
      loadMonth(); loadMap(); setRefreshKey((k) => k + 1);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi dọn dữ liệu', 'danger'); }
    finally { setImporting(false); }
  }

  async function saveHolidays() {
    try { await setHolidays(month, holidays); toast('Đã lưu số ngày nghỉ lễ'); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi lưu nghỉ lễ', 'danger'); }
  }
  // Chọn file -> đọc trước để lấy tháng -> mở modal cho kế toán XÁC NHẬN tháng rồi mới ghi.
  async function onFilesSelected(files) {
    const list = files ? Array.from(files) : [];
    if (!list.length) return;
    setImporting(true);
    try {
      const parsed = [];
      for (const file of list) {
        const buf = await file.arrayBuffer();
        const p = await parseAttendanceXlsx(buf);
        parsed.push({ name: file.name, month: p.month, count: p.employees.length, employees: p.employees });
      }
      openModal(<ImportConfirm files={parsed} defaultMonth={month} onClose={closeModal} onConfirm={(chosen) => doImport(parsed, chosen)} />);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi đọc file', 'danger'); }
    finally { setImporting(false); }
  }
  // Đổi YYYY-MM của mọi ngày sang tháng đã chọn (giữ ngày trong tháng) + tính lại thứ.
  function remapMonth(days, mth) {
    return (days || []).filter((d) => /-(\d{2})$/.test(d.date || '')).map((d) => {
      const dd = d.date.slice(-2);
      const date = `${mth}-${dd}`;
      return { ...d, date, weekday: new Date(`${date}T00:00:00`).getDay() };
    });
  }
  async function doImport(parsed, chosen) {
    if (!/^\d{4}-\d{2}$/.test(chosen || '')) return;
    closeModal();
    setImporting(true);
    try {
      let codes = 0, okFiles = 0;
      for (const p of parsed) {
        if (!p.employees.length) continue;
        const employees = p.employees.map((e) => ({ ...e, days: remapMonth(e.days, chosen) }));
        const r = await importAttendance({ month: chosen, employees });
        codes += (r.scannedCodes || 0); okFiles += 1;
      }
      if (!okFiles) { toast('Không đọc được dữ liệu hợp lệ trong file', 'danger'); return; }
      // GÁN TAY 100%: import chỉ nạp mã vào bảng ánh xạ, chưa ghi công cho ai — kế toán gán từng mã.
      toast(`Đã nạp ${codes} mã từ ${okFiles} file vào bảng ánh xạ — hãy GÁN nhân viên cho từng mã để lên bảng`);
      setMonth(chosen);
      loadMonth(); loadMap(); setRefreshKey((k) => k + 1); setShowMap(true);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi import file', 'danger'); }
    finally { setImporting(false); }
  }
  const loadInbox = () => { if (canApprove) attInbox().then((d) => setInbox(d.items)).catch(() => {}); };
  useEffect(loadInbox, []); // eslint-disable-line

  // VẼ ĐỦ CẢ THÁNG, kể cả ngày CHƯA có dữ liệu chấm công và ngày TƯƠNG LAI — để nhân viên bấm
  // vào NGÀY BẤT KỲ xin nghỉ / giải trình TRƯỚC (VD xin phép cho tuần sau). Ô trống vẫn bấm được;
  // chấm đỏ "đã gửi đơn" hiện cả trên ô trống (đơn tương lai chưa có bản ghi chấm công).
  const dayMap = {};
  data.days.forEach((d) => (dayMap[Number(d.date.slice(-2))] = d));
  const propMap = data.proposals || {};
  // Hôm nay (giờ máy NV) — ngày LÀM VIỆC TRƯỚC hôm nay mà không chấm công sẽ tô ĐỎ để NV nắm ngay.
  const _n = new Date();
  const todayStr = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
  const [my, mm] = month.split('-').map(Number);
  const dim = my && mm ? new Date(my, mm, 0).getDate() : 0;
  const cells = [];
  if (dim) {
    const lead = colOf(new Date(my, mm - 1, 1).getDay());
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let n = 1; n <= dim; n++) {
      const date = `${month}-${String(n).padStart(2, '0')}`;
      cells.push(dayMap[n] || { date, weekday: new Date(my, mm - 1, n).getDay(), empty: true, proposal: propMap[date] || null });
    }
  }

  async function submitProposal(date, type, reason, waives) {
    try { await createAttProposal({ date, type, reason, waives }); toast('Đã gửi đề xuất, chờ duyệt'); closeModal(); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi gửi đề xuất', 'danger'); }
  }
  function clickDay(d) {
    if (!d) return; // bấm ô -> LUÔN mở chi tiết ngày read-only (kể cả tháng đã khoá / mọi trạng thái)
    const openPropose = () => openModal(
      <ProposalModal types={cfg.types} date={d.date} viol={d.viol} checkCount={d.checkCount} day={d.empty ? null : d} onClose={closeModal} onSubmit={(t, r, w) => submitProposal(d.date, t, r, w)} />
    );
    openModal(
      <DayDetailModal date={d.date} day={d.empty ? null : d} proposal={d.proposal} types={cfg.types} canPropose={canPropose} onClose={closeModal} onPropose={openPropose} />
    );
  }
  async function decide(id, action) {
    try { await decideAttProposal(id, action); toast(action === 'approve' ? 'Đã duyệt đơn' : 'Đã từ chối'); loadInbox(); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi xử lý đơn', 'danger'); }
  }

  const s = data.stats || {};
  return (
    <>
      <div className="card" ref={calRef}>
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Quản lý: KHÔNG còn dropdown chọn tên (trùng bảng toàn phòng bên dưới — bấm tên trong bảng
                là xem lịch người đó). Ở đây chỉ HIỆN đang xem lịch của ai + nút quay về lịch của mình. */}
            {/* BỎ HẲN nhãn "Lịch của <tên>" (theo yêu cầu) — biết đang xem lịch ai qua dòng được tô sáng
                ở bảng toàn phòng bên dưới. Chỉ giữ nút quay về lịch của mình khi đang xem người khác. */}
            {cfg.employees.length > 1 && !viewingSelf && cfg.me.canAttendance && (
              <button className="btn sm ghost" onClick={() => setUserId('')} title="Xem lịch chấm công của tôi">🗓️ Lịch của tôi</button>
            )}
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
            {canManage && (
              <span className="row" style={{ gap: 4, alignItems: 'center' }} title="Số ngày nghỉ lễ trong tháng">
                <span className="small muted">Nghỉ lễ</span>
                <input type="number" min="0" max="31" value={holidays} onChange={(e) => setHol(Number(e.target.value))} style={{ width: 56 }} />
                <button className="btn sm ghost" onClick={saveHolidays}>Lưu</button>
              </span>
            )}
            {canManage && (
              <button className="btn sm ghost" title="Chọn nhân viên được MIỄN tính đi muộn (đi muộn không bị phạt)"
                onClick={() => openModal(<LateExemptModal onClose={closeModal} toast={toast} onSaved={() => setRefreshKey((k) => k + 1)} />)}>
                ⏰ Miễn đi muộn
              </button>
            )}
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {cfg.me?.canAttendance && (
              <button className="btn sm" onClick={() => openModal(<FaceAttendanceModal onClose={closeModal} toast={toast} />)} title="Chấm công vào/ra bằng khuôn mặt">
                📷 Chấm công online
              </button>
            )}
            {(me.role === 'GD' || me.role === 'CV') && (
              <button className="btn sm ghost" onClick={() => openModal(<OnlineAttAdminModal onClose={closeModal} toast={toast} />)} title="Cấu hình chấm công online + nhật ký">
                ⚙️ Cấu hình online
              </button>
            )}
            {canPropose && <span className="small muted">Bấm vào 1 ngày để đề xuất / giải trình</span>}
            {locked && (
              <span className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontWeight: 600, color: '#fff', background: 'var(--danger)' }}
                title={`Đã chốt${lock.lockedBy ? ` bởi ${lock.lockedBy}` : ''} — không thể sửa`}>
                🔒 Đã chốt {lock.lockedBy ? `· ${lock.lockedBy}` : ''}
              </span>
            )}
            {canManage && !locked && (
              <button className="btn sm ghost danger" disabled={importing} onClick={purgeMonth}
                title="Xoá chấm công CHƯA chốt của tháng để up lại sạch — dùng khi bị trùng mã / dữ liệu sai (giữ ngày đã duyệt + ánh xạ)">
                🧹 Dọn tháng
              </button>
            )}
            {canManage && !locked && (
              <label className="btn sm" style={{ cursor: 'pointer' }} title="Tự nhận diện VP HN & HCM · chọn được NHIỀU file cùng lúc; xác nhận tháng trước khi ghi">
                {importing ? 'Đang import…' : '⭱ Import Excel chấm công'}
                <input type="file" accept=".xlsx,.xls" multiple style={{ display: 'none' }} disabled={importing} onChange={(e) => { onFilesSelected(e.target.files); e.target.value = ''; }} />
              </label>
            )}
            {canManage && !locked && (
              <button className="btn sm" style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }} onClick={toggleLock}
                title="Chốt bảng chấm công cả tháng — sau khi chốt không thể import/dọn/sửa">
                🔒 Chốt bảng chấm công
              </button>
            )}
            {locked && lock.canUnlock && (
              <button className="btn sm ghost" onClick={toggleLock} title="Chỉ Giám đốc — mở khoá để sửa lại">🔓 Mở khoá</button>
            )}
          </div>
        </div>

        {!canManage && (<>
        <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
          <div className="stat"><div className="label">Tổng ngày công</div><div className="val">{s.tongNgayCong ?? '—'}</div></div>
          <div className="stat"><div className="label">Ngày đi làm</div><div className="val" style={{ color: 'var(--ok)' }}>{s.diLam ?? '—'}</div></div>
          <div className="stat"><div className="label">Ngày nghỉ</div><div className="val" style={{ color: 'var(--danger)' }}>{s.nghi ?? '—'}</div></div>
          <div className="stat" title="Tổng số LỖI: đi muộn / về sớm / quên chấm (3 lỗi riêng biệt, 50.000đ/lỗi) — đã trừ 3 lần muộn miễn/tháng. Quên chấm chỉ tính lỗi khi ĐÃ giải trình lấy lại đủ công; chưa giải trình thì nửa công, không mất tiền."><div className="label">Lỗi bị phạt</div><div className="val" style={{ color: 'var(--warn)' }}>{s.biPhat ?? '—'}</div></div>
          <div className="stat"><div className="label">🏖 Phép còn lại</div><div className="val" style={{ color: 'var(--accent)' }}>{data.leaveBalance ?? 0}</div></div>
        </div>

        {loading ? <div className="empty">Đang tải…</div> : !cells.length ? (
          <div className="empty">Chọn tháng để xem lịch.</div>
        ) : (
          <>
            {!data.days.length && (
              <div className="small muted" style={{ marginBottom: 8 }}>
                Tháng này chưa có dữ liệu chấm công — vẫn bấm được vào ngày bất kỳ để xin nghỉ / giải trình trước.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 5 }}>
              {WD.map((w) => <div key={w} style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--muted2)' }}>{w}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
              {cells.map((d, i) => {
                if (!d) return <div key={`b${i}`} style={{ minHeight: 92 }} />;
                // MÀU Ô (bảng lịch bao quát cho nhân viên):
                //  xanh = đi làm đủ · nửa xanh = 1/2 công · VÀNG = có lỗi phạt (ghi rõ lỗi trong ô)
                //  ĐỎ = nghỉ / NGÀY LÀM VIỆC ĐÃ QUA KHÔNG CHẤM CÔNG (kể cả chưa có bản ghi — để NV nắm ngay)
                //  xám = CN / T7 nghỉ ĐK · trắng = ngày chưa tới (bấm để xin nghỉ trước)
                const past = d.date < todayStr;
                const missedWork = d.empty && past && d.weekday >= 1 && d.weekday <= 5; // T2–T6 đã qua, không chấm
                const hasPen = !d.empty && (d.phat || 0) > 0;
                const halfDay = !d.empty && d.cong === 0.5 && !hasPen; // nửa công (làm 1 buổi / chấm 1 lần), chưa lỗi -> ½ xanh
                const st = d.empty
                  ? (d.weekday === 0 || (past && d.weekday === 6) ? ST.off
                    : missedWork ? ST.absent
                    : { bg: 'transparent', bd: 'var(--border)', tx: 'var(--muted2)' })
                  : hasPen ? ST.warn : halfDay ? ST.half : (ST[d.status] || ST.off);
                return (
                  <div key={d.date} onClick={() => clickDay(d)}
                    title="Bấm để xem chi tiết ngày (đề xuất, chấm công, kế toán chốt)"
                    style={{ minHeight: 92, borderRadius: 8, border: `1px solid ${st.bd}`, background: halfDay || d.status === 'half' ? HALF_BG : st.bg, padding: '6px 8px', position: 'relative', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: st.tx }}>{Number(d.date.slice(-2))}</div>
                    <div style={{ fontSize: 11, lineHeight: 1.3, color: st.tx }}>
                      {d.empty ? (d.weekday === 0 ? 'CN' : past && d.weekday === 6 ? 'T7' : missedWork ? 'Không chấm công' : '') : labelOf(d)}
                    </div>
                    {/* LỖI PHẠT ghi CHI TIẾT từng dòng trong ô (đi muộn / về sớm / quên chấm công). */}
                    {hasPen && VIOL_LABELS.filter(([k]) => d.viol?.[k]).map(([k, l]) => (
                      <div key={k} title="50.000đ/lỗi" style={{ fontSize: 10.5, color: 'var(--danger)', fontWeight: 700, lineHeight: 1.3 }}>{l}</div>
                    ))}
                    {/* Dữ liệu theo IP (mốc ở cty + vắng): CHỈ kế toán/GĐ thấy; nhân viên chỉ thấy dữ liệu chấm công. */}
                    {canManage && d.awayHours > 0 && <div title={`Vắng mặt ở công ty (Wifi/IP): ${(d.awayLog || []).join(', ')}`} style={{ fontSize: 9.5, color: 'var(--warn)', fontWeight: 700 }}>⚠ vắng {d.awayHours}h</div>}
                    {canManage && d.firstSeenCompany && <div title="Mốc ping ở công ty (tham khảo, không thay check-in/out)" style={{ fontSize: 9, color: 'var(--muted2)' }}>🏢 {d.firstSeenCompany}–{d.lastSeenCompany || d.firstSeenCompany}</div>}
                    {d.adminAdjusted ? <span title="Kế toán đã xác nhận" style={{ position: 'absolute', top: 3, right: 6, fontSize: 13, fontWeight: 800, color: 'var(--ok)', lineHeight: 1 }}>✓</span>
                      : d.approved ? <span title="Có đơn được duyệt" style={{ position: 'absolute', top: 5, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} />
                      : d.proposal && d.proposal.status === 'pending' ? <span title="Đã gửi đề xuất, chờ duyệt" style={{ position: 'absolute', top: 5, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} /> : null}
                  </div>
                );
              })}
            </div>
            <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
              <span><i style={{ color: 'var(--ok)' }}>●</i> Đi làm đủ công</span>
              <span><i style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, verticalAlign: '-1px', background: HALF_BG, border: '1px solid color-mix(in srgb, var(--ok) 40%, transparent)' }} /> 1/2 công (làm 1 buổi / chấm 1 lần)</span>
              <span><i style={{ color: 'var(--warn-fill)' }}>●</i> Có lỗi phạt — lỗi ghi rõ trong ô</span>
              <span><i style={{ color: 'var(--danger)' }}>●</i> Nghỉ / không chấm công</span>
              <span><i style={{ color: 'var(--muted2)' }}>●</i> CN / T7 nghỉ ĐK</span>
              <span>ô trắng = ngày chưa tới (bấm để xin nghỉ trước)</span>
              <span><i style={{ color: 'var(--danger)' }}>●</i> chấm đỏ góc = đã gửi đề xuất / được duyệt</span>
            </div>
          </>
        )}
        </>)}
      </div>

      {canManage && <AttendanceMatrix month={month} refreshKey={refreshKey} toast={toast} openModal={openModal} closeModal={closeModal} locked={locked}
        onPickEmp={(id) => { setUserId(id); setTimeout(() => calRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }} />}

      {canManage && (
        <div className="card">
          <div className="row between" style={{ cursor: 'pointer' }} onClick={() => setShowMap((v) => !v)}>
            <h3 style={{ margin: 0 }}>🔗 Ánh xạ mã chấm công ↔ nhân viên ({empmap.mappings.length})
              {(() => { const n = empmap.mappings.filter((m) => !(m.user?._id || m.user)).length; return n ? <span className="small" style={{ color: 'var(--danger)', marginLeft: 8, fontWeight: 600 }}>· {n} chưa gán</span> : null; })()}
            </h3>
            <span className="small muted">{showMap ? '▲ Thu gọn' : '▼ Mở'}</span>
          </div>
          {showMap && (
            <>
              <div className="small muted" style={{ margin: '6px 0 10px' }}>
                Mã + tên quét từ Excel. <b>Hệ thống KHÔNG tự gán</b> — chọn nhân viên cho từng mã thì chấm công của mã đó mới lên bảng (tránh nhầm do trùng mã). Mã đã gán sẽ <b>KHOÁ</b> (🔒) để khỏi đổi/ghi đè nhầm — bấm 🔒 mở khoá mới đổi/gỡ được người; <b>↻</b> = áp lại cho đúng người đó sau khi import lại.
              </div>
              {!empmap.mappings.length ? <div className="empty">Chưa có mã nào — import Excel để nạp danh sách.</div> : (
                <div className="tbl-wrap framed">
                  <table style={{ fontSize: 13 }}>
                    <thead><tr><th style={{ width: 110 }}>Mã (Excel)</th><th>Tên (Excel)</th><th style={{ width: 300 }}>Nhân viên FunnyTask</th></tr></thead>
                    <tbody>
                      {[...empmap.mappings].sort((a, b) => (!(a.user?._id || a.user) ? 0 : 1) - (!(b.user?._id || b.user) ? 0 : 1) || String(a.excelCode).localeCompare(String(b.excelCode))).map((m) => (
                        <tr key={m.excelCode} style={{ background: !(m.user?._id || m.user) ? 'color-mix(in srgb, var(--danger) 6%, transparent)' : undefined }}>
                          <td><code style={{ color: 'var(--warn)' }}>{m.excelCode}</code></td>
                          <td>{m.excelName || '—'}</td>
                          <td>
                            {(() => {
                              const assigned = !!(m.user?._id || m.user);
                              const rowLocked = assigned && !unlockedCodes.has(m.excelCode); // đã gán + chưa mở khoá
                              return (
                            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                              <select value={m.user?._id || m.user || ''} disabled={locked || rowLocked || savingCode === m.excelCode} onChange={(e) => assignMap(m.excelCode, e.target.value, true)} style={{ flex: 1 }}>
                                <option value="">— Chưa gán —</option>
                                {empmap.roster.map((u) => (
                                  <option key={u._id} value={u._id}>{u.name}{u.employeeCode ? ` (${u.employeeCode})` : ''}{u.isDeputy ? ' · PP' : u.role === 'TP' ? ' · TP' : ''}</option>
                                ))}
                              </select>
                              {savingCode === m.excelCode ? (
                                <span className="small muted" style={{ whiteSpace: 'nowrap' }}>Đang lưu…</span>
                              ) : savedCode === m.excelCode ? (
                                <span className="small" style={{ color: 'var(--ok, #16a34a)', whiteSpace: 'nowrap' }}>✓ Đã lưu</span>
                              ) : null}
                              {assigned && !locked && savingCode !== m.excelCode && (rowLocked
                                ? <button className="btn sm ghost" title="Đã khoá — bấm để mở khoá rồi mới đổi/gỡ được người" onClick={() => setUnlockedCodes((s) => new Set(s).add(m.excelCode))}>🔒</button>
                                : <button className="btn sm ghost" title="Đang mở khoá — bấm để khoá lại" onClick={() => setUnlockedCodes((s) => { const n = new Set(s); n.delete(m.excelCode); return n; })}>🔓</button>
                              )}
                              {assigned && !locked && !rowLocked && savingCode !== m.excelCode && (
                                <button className="btn sm ghost" title="Áp lại chấm công vừa import cho người này (dùng sau khi import lại)" onClick={() => assignMap(m.excelCode, m.user?._id || m.user)}>↻</button>
                              )}
                            </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {canApprove && (
        <div className="card">
          <h3>📥 Đơn chờ duyệt ({inbox.length})</h3>
          {!inbox.length ? <div className="empty">Không có đơn nào chờ bạn duyệt.</div> : inbox.map((p) => (
            <div key={p._id} className="row between" style={{ padding: '8px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <b>{p.user?.name}</b> <span className="small muted">{p.user?.isDeputy ? '(PP)' : p.user?.role === 'TP' ? '(TP)' : ''}</span> · {p.date.split('-').reverse().join('/')}
                <div className="small"><b style={{ color: 'var(--accent)' }}>{p.typeLabel}</b>{p.reason ? ` — ${p.reason}` : ''}</div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn sm" onClick={() => decide(p._id, 'approve')}>✓ Duyệt</button>
                <button className="btn sm ghost danger" onClick={() => decide(p._id, 'reject')}>✕ Từ chối</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
